import { ComponentProps, FC, SyntheticEvent, useMemo, useState } from 'react';
import { Autocomplete, Chip, Stack, StackProps, styled, TextField, Typography } from '@mui/material';

import { KeyValueFilter, KeyValuePair, KeyValuePairSerialized } from '@playq/octopus-common';

import { IFilterCellProps } from '/shared/Table/components/TableHeadCell/IFilterCellProps';
import { FilterForm } from '/shared/Table/components/TableHeadCell/styles';
import { FilterData } from '/shared/Table/interfaces/FilterData';

const Wrapper = styled('div')`
  width: 100%;
  max-width: 460px;
`;

const SelectStyled = styled(Autocomplete<string, false, false, true>)`
  min-width: 150px;
  & .MuiAutocomplete-clearIndicator {
    display: none;
  }

  & .MuiAutocomplete-inputRoot {
    padding: 0 !important;
  }
`;

const CommonSelectStyled: FC<Omit<ComponentProps<typeof SelectStyled>, 'renderInput'>> = (props) => (
  <SelectStyled
    slotProps={{
      popper: {
        popperOptions: {
          placement: 'bottom-end',
        },
      },
      paper: { style: { width: 'fit-content', minWidth: '150px' } },
    }}
    {...props}
    renderInput={(params) => <TextField {...params} variant='standard' placeholder='Select' />}
  />
);

const Row = (props: StackProps) => <Stack direction='row' gap='2px' alignItems='center' {...props} />;

const SelectedWrapper = styled('div')`
  border-bottom: 2px solid ${({ theme }) => theme.palette.text.primary};
  padding-bottom: ${({ theme }) => theme.spacing(1)};
`;

const emptyValueValue = 'All';

export const makeKeyValuePair = (key: string, value: string | undefined) => {
  const newKeyValuePair = new KeyValuePair();
  newKeyValuePair.key = key;
  newKeyValuePair.value = value === emptyValueValue ? undefined : value;
  return newKeyValuePair;
};

const setChipColor = (key: string) => {
  const isRequired = key.startsWith('$');

  return isRequired ? 'secondary' : 'primary';
};

/**
 * @param filterData should be a string[] with every child is a stringified KeyValuePairSerialized
 */

const parseKeyValuesFilterData = (filterData: FilterData) =>
  filterData
    .map((keyValuePairAsString) => {
      try {
        const keyValuePairSerialized = JSON.parse(String(keyValuePairAsString)) as KeyValuePairSerialized;
        return new KeyValuePair(keyValuePairSerialized);
      } catch {
        return null;
      }
    })
    .filter(Boolean) as KeyValuePair[];

export function KeyValueFilterCell({ filter, filterData, onSubmit, onClear }: IFilterCellProps) {
  const keyValuePairs = useMemo(() => parseKeyValuesFilterData(filterData || []), [filterData]);

  const selected = useMemo(() => {
    if (!filter) {
      return [];
    }
    return (filter as KeyValueFilter).pairs;
  }, [filter]);

  const unavailableKeys = useMemo(() => {
    const result: string[] = [];
    selected.forEach(({ key, value }) => {
      if (!value) {
        result.push(key);
      }
    });
    return result;
  }, [selected]);

  const [selectedKey, setSelectedKey] = useState<string>();
  const [valueMenuOpen, setValueMenuOpen] = useState(false);

  const keyOptions = useMemo(() => {
    const allKeyOptions = keyValuePairs.map(({ key }) => key).filter((key) => !unavailableKeys.includes(key));
    const uniqueKeyOptions = Array.from(new Set(allKeyOptions));

    return [...uniqueKeyOptions].sort();
  }, [keyValuePairs, unavailableKeys]);

  const valueOptions = useMemo(() => {
    if (!selectedKey) {
      return [];
    }
    const allValueOptions = keyValuePairs
      .filter(({ key, value }) => key === selectedKey && value !== undefined)
      .map(({ value }) => value as string);

    const uniqueValueOptions = Array.from(new Set(allValueOptions));
    const valueOptionsWithoutSelected = uniqueValueOptions.filter(
      (value) => !selected.some((pair) => pair.value === value && pair.key === selectedKey)
    );

    return [...valueOptionsWithoutSelected, emptyValueValue].sort();
  }, [selectedKey, keyValuePairs, selected]);

  const handleKeyChange = (_e: SyntheticEvent, newKey: string | null) => {
    setSelectedKey(newKey ?? undefined);
  };

  const handleValueChange = (value: string | null) => () => {
    if (!selectedKey || !value) {
      return;
    }
    const newKeyValuePair = makeKeyValuePair(selectedKey, value);
    const newFilter = new KeyValueFilter();

    const filteredSelected = selected.filter((pair) => {
      // return all pairs with another key
      if (pair.key !== selectedKey) {
        return true;
      }
      // if 'All' is selected, clear other pairs with the same key
      if (value === emptyValueValue) {
        return false;
      }
      // remove 'All' if exist
      return pair.value !== undefined;
    });

    newFilter.pairs = filteredSelected.concat(newKeyValuePair);
    onSubmit(newFilter, false);
    setValueMenuOpen(false);
  };

  const handleDelete = (key: string, value: string | undefined) => () => {
    const newFilter = new KeyValueFilter();
    const newPairs = selected.filter((pair) => pair.key !== key || pair.value !== value);
    newFilter.pairs = newPairs;
    if (!newPairs.length) {
      onClear();
      return;
    }
    onSubmit(newFilter, false);
  };

  const handleValueMenuOpen = () => setValueMenuOpen(true);
  const handleValueMenuClose = () => setValueMenuOpen(false);

  return (
    <FilterForm>
      <Wrapper>
        {selected.length > 0 && (
          <SelectedWrapper>
            {selected.map(({ key, value }) => (
              <Chip
                key={key + (value || '')}
                label={`${key}: ${value || emptyValueValue}`}
                onDelete={handleDelete(key, value)}
                color={setChipColor(key)}
              />
            ))}
          </SelectedWrapper>
        )}
        <Row>
          <Row>
            <Typography variant='body1'>Key: </Typography>
            <CommonSelectStyled
              value={selectedKey ?? null}
              options={keyOptions}
              freeSolo={true}
              onChange={handleKeyChange}
            />
          </Row>
          <Row>
            <Typography variant='body1'>Value: </Typography>
            <CommonSelectStyled
              open={valueMenuOpen}
              value={null}
              options={valueOptions}
              filterSelectedOptions={false}
              renderOption={(props, option) => (
                <li {...props} key={option} onClick={handleValueChange(option)}>
                  {option}
                </li>
              )}
              onOpen={handleValueMenuOpen}
              onClose={handleValueMenuClose}
            />
          </Row>
        </Row>
      </Wrapper>
    </FilterForm>
  );
}

KeyValueFilterCell.type = KeyValueFilter.ClassName;
